import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Send, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { SITE } from "../../lib/content";
import { MaskLine, SectionLabel } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CLASSES = ["Class 9", "Class 10", "Class 11", "Class 12"];
const SUBJECTS = ["Mathematics", "Science", "Physics (CBSE)", "Physics (AHSEC)"];

const empty = { name: "", student_class: "", subject: "", phone: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.student_class || !form.subject || !form.phone || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Enquiry sent! We'll get back to you shortly.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-ink/15 rounded-lg px-4 py-3 text-ink placeholder:text-ink/40 focus:border-flame outline-none transition-colors";

  return (
    <section id="contact" data-testid="contact-section" className="bg-white py-24 lg:py-32 border-t border-ink/10">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12">
        {/* Left */}
        <div className="lg:col-span-5">
          <SectionLabel>Contact</SectionLabel>
          <h2 className="font-heading font-black tracking-tighter text-ink text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
            <MaskLine>Let&apos;s</MaskLine>
            <MaskLine delay={0.1}>Talk<span className="text-flame">.</span></MaskLine>
          </h2>
          <p className="mt-6 text-ink/60 text-lg max-w-sm">
            Admissions are open. Reach out to enrol, book a free demo or ask anything.
          </p>

          <div className="mt-10 space-y-5">
            <a href={`tel:${SITE.phoneIntl}`} data-testid="contact-phone" className="flex items-start gap-4 group">
              <span className="grid place-items-center h-11 w-11 rounded-lg bg-ink text-white group-hover:bg-flame transition-colors"><Phone className="h-5 w-5" /></span>
              <div>
                <div className="text-xs uppercase tracking-widest text-ink/40">Phone</div>
                <div className="font-semibold text-ink group-hover:text-flame transition-colors">{SITE.phone}</div>
              </div>
            </a>
            <a href={`mailto:${SITE.email}`} data-testid="contact-email" className="flex items-start gap-4 group">
              <span className="grid place-items-center h-11 w-11 rounded-lg bg-ink text-white group-hover:bg-flame transition-colors"><Mail className="h-5 w-5" /></span>
              <div>
                <div className="text-xs uppercase tracking-widest text-ink/40">Email</div>
                <div className="font-semibold text-ink group-hover:text-flame transition-colors break-all">{SITE.email}</div>
              </div>
            </a>
            <div className="flex items-start gap-4">
              <span className="grid place-items-center h-11 w-11 rounded-lg bg-ink text-white"><MapPin className="h-5 w-5" /></span>
              <div>
                <div className="text-xs uppercase tracking-widest text-ink/40">Address</div>
                <div className="font-semibold text-ink">{SITE.address}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right form */}
        <div className="lg:col-span-7">
          <form onSubmit={submit} data-testid="contact-form" className="bg-paper border border-ink/10 p-6 sm:p-8 rounded-2xl">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-ink/70">Name</label>
                <input
                  data-testid="input-name"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="Your full name"
                  className={`mt-1.5 ${inputCls}`}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-ink/70">Phone Number</label>
                <input
                  data-testid="input-phone"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  placeholder="10-digit number"
                  className={`mt-1.5 ${inputCls}`}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-ink/70">Class</label>
                <Select value={form.student_class} onValueChange={(v) => set("student_class", v)}>
                  <SelectTrigger data-testid="select-class" className="mt-1.5 bg-white border-ink/15 h-[46px]">
                    <SelectValue placeholder="Select class" />
                  </SelectTrigger>
                  <SelectContent>
                    {CLASSES.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`class-opt-${c}`}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-ink/70">Subject</label>
                <Select value={form.subject} onValueChange={(v) => set("subject", v)}>
                  <SelectTrigger data-testid="select-subject" className="mt-1.5 bg-white border-ink/15 h-[46px]">
                    <SelectValue placeholder="Select subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {SUBJECTS.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`subject-opt-${s}`}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4">
              <label className="text-sm font-medium text-ink/70">Message</label>
              <textarea
                data-testid="input-message"
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
                placeholder="Tell us how we can help..."
                rows={4}
                className={`mt-1.5 resize-none ${inputCls}`}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="submit-contact"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-flame text-white font-semibold px-6 py-4 rounded-full hover:bg-ink transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4" />}
              {loading ? "Sending..." : "Send Enquiry"}
            </button>
          </form>
        </div>
      </div>

      {/* Map */}
      <div className="max-w-7xl mx-auto px-6 mt-12">
        <div data-testid="map-embed" className="overflow-hidden rounded-2xl border border-ink/10 h-[380px]">
          <iframe
            title="NK Science & Physics Academy location"
            src="https://www.google.com/maps?q=Hajo%2C%20Kamrup%2C%20Assam&output=embed"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </section>
  );
}
